import { FC } from 'react';
import moment from 'moment';
import { groupBy } from 'lodash';
import { useNavigate } from 'react-router';
import { Checkbox, HStack, Flex, Image, Icon, Text, VStack, Box, Tooltip, Tag } from '@chakra-ui/react';
import icons from 'shared/icons';
import { linkStyle } from 'shared/styles';
import { TPictures } from 'core/API/picturesApi';
import { useTranslation } from 'helpers/languages/translations';

const MImageCard: FC<{
    picture: TPictures;
    pictureGroup?: TPictures[];
    nbPictures?: number;
    isChecked?: boolean;
    isIndeterminate?: boolean;
    onCheck?: (picture: string[]) => void;
}> = ({ isChecked, isIndeterminate, picture, nbPictures, pictureGroup, onCheck }) => {
    const navigate = useNavigate();
    const translate = useTranslation();
    const PhotoIcon = icons.photo;

    const onPicCheck = () => {
        if (nbPictures && nbPictures > 1) {
            const ids = pictureGroup?.map((pg) => pg._id as string) || [];
            onCheck?.(ids);
        } else {
            picture._id && onCheck?.([picture._id]);
        }
    };

    const checkAllSameValues = (key: keyof TPictures) =>
        pictureGroup?.filter((p) => p[key] && p[key] === pictureGroup[0][key]).length === pictureGroup?.length;

    const positionWarningColor = picture.duplicatePosition ? 'orange.400' : 'inherit';

    return (
        <Flex
            className="pictureCard"
            bg="white"
            fontSize="14px"
            flexDir="column"
            color="gray.600"
            fontWeight="400"
            p={4}
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.200"
            mb={6}
            mr={6}
            w="315px"
        >
            <HStack mb={5} alignItems="stretch" justifyContent="space-between" w="100%">
                <Flex alignItems="flex-start" mt={2}>
                    <Checkbox isChecked={isChecked} isIndeterminate={isIndeterminate} onChange={onPicCheck} />
                    {picture.image_url ? (
                        <Flex boxSize="120px" ml={3} justifyContent="center">
                            <Image
                                src={picture.image_url}
                                alt="product image"
                                objectFit="contain"
                                maxH="100%"
                                maxW="100%"
                            />
                        </Flex>
                    ) : (
                        <Icon as={icons.image} boxSize="120px" />
                    )}
                </Flex>

                <Box>
                    <Flex alignItems="center">
                        {nbPictures && (
                            <Tooltip
                                label={picture.duplicatePosition ? translate('pictures.groupedPositionWarning') : ''}
                            >
                                <Flex>
                                    <Icon as={PhotoIcon} w="20px" h="20px" color={positionWarningColor} />
                                    <Text ml={1} color={positionWarningColor}>
                                        {nbPictures}
                                    </Text>
                                </Flex>
                            </Tooltip>
                        )}
                        {!nbPictures && picture.isFromApi && (
                            <Tag fontSize="xs" ml={2}>
                                Auto
                            </Tag>
                        )}
                        {(picture.hasNoProduct || picture.wrongCompanyId) && (
                            <Tooltip
                                label={translate(
                                    picture.hasNoProduct
                                        ? 'pictures.upload.no_product'
                                        : 'pictures.upload.wrong_company',
                                )}
                            >
                                <Box>
                                    <Icon as={icons.warning} w={6} h={6} color="orange.400" />
                                </Box>
                            </Tooltip>
                        )}
                        {!nbPictures && (
                            <Tooltip label={picture.duplicatePosition ? translate('pictures.duplicatePosition') : ''}>
                                <Flex alignItems="middle">
                                    <Icon as={icons.rank} w="20px" h="20px" color={positionWarningColor} />
                                    <Text color={positionWarningColor}>{picture.position}</Text>
                                </Flex>
                            </Tooltip>
                        )}
                    </Flex>
                </Box>
            </HStack>
            <VStack alignItems="flex-start" w="100%">
                <Text
                    mb={1}
                    {...linkStyle}
                    onClick={() => navigate(`./detail/`, { state: { pictures: pictureGroup, picture: picture } })}
                >
                    {picture.upc || picture.ean}
                </Text>
                <Text mb={1} height="63px" overflow="hidden">
                    {picture.product_name || translate('pictures.noLabel')}
                </Text>
                <Flex alignItems="center">
                    <Text fontSize="12px" color="gray.400">
                        {`${translate(`pictures.${picture.updated_at ? 'updated' : 'created'}_at`)} ${moment(
                            picture.updated_at || picture.created_at,
                        ).format('DD/MM/YYYY')}`}
                    </Text>
                    <Box textTransform="capitalize">
                        <>
                            {nbPictures && (
                                <>
                                    {checkAllSameValues('isFromApi') && (
                                        <Tag fontSize="xs" ml={2}>
                                            Auto
                                        </Tag>
                                    )}
                                    {checkAllSameValues('image_type') && (
                                        <Tag fontSize="xs" ml={2}>
                                            {picture.image_type}
                                        </Tag>
                                    )}
                                </>
                            )}
                            {!nbPictures && (
                                <>
                                    <Tag fontSize="xs" ml={2}>
                                        {picture.image_caractersitics}
                                    </Tag>
                                    <Tag fontSize="xs" ml={2}>
                                        {picture.image_type}
                                    </Tag>
                                </>
                            )}
                        </>
                    </Box>
                </Flex>
            </VStack>
        </Flex>
    );
};

export default MImageCard;
